/**
 * video-to-sound/progress.js — window.VideoSoundProgress
 *
 * What the page says about a generation that is still on the server. Pure
 * functions over the status payload of /api/video-sound/status|history, so
 * the words and the numbers are tested in node (tests/js/video-sound-progress.test.mjs)
 * and app.js only puts them on screen.
 *
 * Why it exists (2026-09-29, intl generation 1): a 43-second video took
 * 15.5 minutes; the person reloaded the page four times, and after every
 * reload the page knew nothing — the history row said «in progress» and did
 * nothing when clicked (four clicks, rage_click at minute 11). A reloaded
 * page now finds the running generation in its own history and shows it:
 * parts done, time since the ORDER (the server's clock, not the page's), and
 * the remaining time once there is evidence for it.
 *
 *   running(items)           → the newest queued/running item, or null
 *   fraction(progress)       → 0..1, share of vendor calls answered
 *   etaText(sec, T)          → «about 3 min left» / «less than a minute left» / ''
 *   line(progress, T, sec)   → «Parts done: 5 of 17 · Elapsed 2:41 · about 1 min left»
 */
(function (root) {
    'use strict';

    function fmt(t, map) {
        t = String(t == null ? '' : t);
        for (var k in map) if (Object.prototype.hasOwnProperty.call(map, k)) t = t.split(':' + k).join(map[k]);
        return t;
    }
    function clock(s) {
        s = Math.max(0, Math.floor(s || 0));
        var m = Math.floor(s / 60), r = s - m * 60;
        return m + ':' + (r < 10 ? '0' : '') + r;
    }

    function isRunning(it) { return !!it && (it.status === 'queued' || it.status === 'running'); }

    /** History comes newest first; the first one still on the server is the one to follow. */
    function running(items) {
        var list = items || [];
        for (var i = 0; i < list.length; i++) if (isRunning(list[i])) return list[i];
        return null;
    }

    function fraction(pr) {
        pr = pr || {};
        var total = +pr.total || 0, done = +pr.done || 0;
        if (!(total > 0)) return 0;
        return Math.max(0, Math.min(1, done / total));
    }

    /* Minutes are rounded UP: «about 1 min» for 20 s would be a promise kept,
       «about 0 min» never is. Under a minute gets its own words. */
    function etaText(sec, T) {
        if (typeof sec !== 'number' || !isFinite(sec) || sec <= 0) return '';
        T = T || {};
        if (sec < 60) return T.eta_soon || '';
        return fmt(T.eta_min_fmt || '', { n: Math.ceil(sec / 60) });
    }

    /* Waiting in the vendor's queue, nothing started yet: the place, then the
       time left once the queue has moved enough to measure (server:
       VideoSoundGeneration::queueState), or — when it stands still — for how
       long, so the person can decide to cancel (the hold comes back). Bug report
       23.10.2026: «висит в работе с 0%» — 105 places back for 13.7 min, and the
       page opened from «Мои генерации» said only the time since the order. */
    var STILL_S = 180;   // = VideoSoundGeneration::QUEUE_STILL_AFTER_S
    function queueText(pr, T) {
        pr = pr || {};
        T = T || {};
        var q = pr.queue;
        if (!(typeof q === 'number' && q > 0) || (+pr.done || 0) > 0 || (+pr.running || 0) > 0) return '';
        var parts = [fmt(T.queue_fmt || '', { n: q })];
        var eta = etaText(pr.queue_eta_s, T);
        if (eta) parts.push(eta);
        else if (typeof pr.queue_still_s === 'number' && pr.queue_still_s >= STILL_S && T.queue_still_fmt) {
            parts.push(fmt(T.queue_still_fmt, { m: Math.floor(pr.queue_still_s / 60) }));
        }
        // No Premium, and the engine has another road: Premium is sent there
        // sooner (RoutedProvider, 24.10.2026) — the one queue where it is true.
        if (pr.premium_skips && T.queue_premium_skip) parts.push(T.queue_premium_skip);
        return parts.join(' · ');
    }

    function line(pr, T, sec) {
        pr = pr || {};
        T = T || {};
        var waiting = queueText(pr, T);
        if (waiting) {
            var w = [waiting];
            if (typeof pr.elapsed_s === 'number' && pr.elapsed_s >= 0) w.push(fmt(T.elapsed_fmt, { s: clock(pr.elapsed_s) }));
            return w.join(' · ');
        }
        var parts = [];
        if ((+pr.total || 0) > 1) parts.push(fmt(T.progress_fmt, { done: +pr.done || 0, total: +pr.total }));
        if (typeof pr.elapsed_s === 'number' && pr.elapsed_s >= 0) parts.push(fmt(T.elapsed_fmt, { s: clock(pr.elapsed_s) }));
        var eta = etaText(sec, T);
        if (eta) parts.push(eta);
        return parts.join(' · ');
    }

    var api = { running: running, isRunning: isRunning, fraction: fraction, etaText: etaText, queueText: queueText, line: line, clock: clock };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.VideoSoundProgress = api;
})(typeof window !== 'undefined' ? window : this);
